import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle, XCircle, Clock, X, BookOpen, Calculator } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import MathText from '@/components/MathText';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TestReview() {
  const { sessionId, section, moduleNum } = useParams();
  const navigate = useNavigate();
  const fetchedRef = useRef(false);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  // Set dynamic page title based on test type and module
  useEffect(() => {
    if (results) {
      const testTitle = results.test_title || 'Test';
      const sectionName = section === 'rw' ? 'Reading & Writing' : 'Math';
      
      if (section && moduleNum) {
        document.title = `${sectionName} Module ${moduleNum} Review | Practice4SAT`;
      } else if (testTitle.toLowerCase().includes('diagnostic')) {
        document.title = 'Diagnostic Test Review | Practice4SAT';
      } else if (testTitle.match(/Practice Test (\d+)/)) {
        const testNumber = testTitle.match(/Practice Test (\d+)/)[1];
        document.title = `Practice Test ${testNumber} Review | Practice4SAT`;
      } else {
        document.title = 'Test Review | Practice4SAT';
      }
    }
  }, [results, section, moduleNum]);

  useEffect(() => {
    // Prevent double fetch in React Strict Mode
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchResults();
  }, [sessionId]);

  useEffect(() => {
    if (results && results.questions) {
      filterQuestionsByModule();
    }
  }, [results, section, moduleNum]);

  const fetchResults = async () => {
    try {
      // Try to get diagnostic session first
      try {
        const diagnosticResponse = await axios.get(`${API}/diagnostic/${sessionId}/score`);
        if (diagnosticResponse.data) {
          const diagnosticSession = await axios.get(`${API}/diagnostic/${sessionId}/review`);
          setResults(diagnosticSession.data);
          return;
        }
      } catch (error) {
        console.debug('Not a diagnostic session, loading regular test');
      }
      
      // Regular test session
      const sessions = await axios.get(`${API}/test-sessions/${sessionId}`);
      const testId = sessions.data.test_id;
      
      const sessionResponse = await axios.get(`${API}/tests/${testId}/results/${sessionId}`);
      setResults(sessionResponse.data);
    } catch (error) {
      toast.error('Failed to load review');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const filterQuestionsByModule = () => {
    if (!results || !results.questions) return;

    // If no section/module specified, show all questions (shouldn't happen with new routing)
    if (!section || !moduleNum) {
      setFilteredQuestions(results.questions);
      return;
    }

    const filtered = results.questions.filter(q => {
      const qSection = q.section || 'math';
      const qModule = q.module || 'module_1';
      
      // Check section match
      if (qSection !== section) return false;
      
      // Check module match
      if (moduleNum === '1') {
        return qModule === 'module_1';
      } else {
        // Module 2 includes module_2a and module_2b
        return qModule === 'module_2a' || qModule === 'module_2b' || qModule === '2A' || qModule === '2B';
      }
    });

    // Deduplicate by question id (just in case)
    const seen = new Set();
    const uniqueFiltered = filtered.filter(q => {
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    });

    setFilteredQuestions(uniqueFiltered);
  };

  const [mistakeTags, setMistakeTags] = useState({});

  const tagOptions = [
    { value: 'careless_error', label: 'Careless Error', color: '#f59e0b' },
    { value: 'didnt_know_concept', label: "Didn't Know Concept", color: '#ef4444' },
    { value: 'ran_out_of_time', label: 'Ran Out of Time', color: '#6366f1' },
    { value: 'misread_question', label: 'Misread Question', color: '#8b5cf6' },
  ];

  const handleMistakeTag = async (questionId, tag) => {
    try {
      // First ensure mistake is collected
      await axios.post(`${API}/mistakes/collect`);
      await axios.put(`${API}/mistakes/${questionId}/${sessionId}/tag`, {
        question_id: questionId, session_id: sessionId, tag
      });
      setMistakeTags(prev => ({ ...prev, [questionId]: tag }));
    } catch (e) {
      // Silently fail - tag will save next time journal is opened
    }
  };

  const handleExit = () => {
    navigate('/dashboard');
  };

  const handleBackToReview = () => {
    navigate(`/review/${sessionId}`);
  };

  const getSectionColor = () => {
    return section === 'rw' ? '#6366f1' : '#0030b3';
  };

  const getSectionName = () => {
    return section === 'rw' ? 'Reading & Writing' : 'Math';
  };

  const SectionIcon = section === 'rw' ? BookOpen : Calculator;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading review...</div>
      </div>
    );
  }

  // Exit button for review header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      data-testid="exit-review-btn"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <X size={20} />
      Exit to Dashboard
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header 
        variant="minimal" 
        logoClickPath="/dashboard"
        rightContent={exitButton}
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div className="review-content-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
          {/* Module Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ 
                background: `${getSectionColor()}15`,
                borderRadius: '12px',
                padding: '0.75rem',
                display: 'inline-flex'
              }}>
                <SectionIcon size={28} color={getSectionColor()} />
              </div>
              <div>
                <h1 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '700', 
                  color: '#1a2332', 
                  margin: 0,
                  fontFamily: 'Arial, sans-serif'
                }} data-testid="review-title">
                  {getSectionName()} - Module {moduleNum}
                </h1>
                <p style={{ 
                  fontSize: '0.9rem', 
                  color: '#666',
                  margin: '0.25rem 0 0 0'
                }}>
                  {filteredQuestions.length} questions • {filteredQuestions.filter(q => q.is_correct).length} correct
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleBackToReview}
              variant="outline"
              data-testid="back-to-review-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: getSectionColor(),
                borderColor: getSectionColor()
              }}
            >
              <ArrowLeft size={18} />
              Back to Review
            </Button>
          </div>

          {/* Questions List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {filteredQuestions.map((question, idx) => (
              <div key={question.id} className="question-card" data-testid={`review-question-${idx}`}>
                <div className="question-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div className="question-number" data-testid={`question-num-${idx}`}>
                      Question {question.question_number}
                    </div>
                    {question.is_correct ? (
                      <Badge style={{ background: '#dcfce7', color: '#166534' }} data-testid={`correct-badge-${idx}`}>
                        <CheckCircle size={14} style={{ marginRight: '0.3rem' }} />
                        Correct
                      </Badge>
                    ) : (
                      <Badge style={{ background: '#fee2e2', color: '#991b1b' }} data-testid={`incorrect-badge-${idx}`}>
                        <XCircle size={14} style={{ marginRight: '0.3rem' }} />
                        Incorrect
                      </Badge>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className={`question-difficulty difficulty-${question.difficulty}`} data-testid={`difficulty-${idx}`}>
                      {question.difficulty}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.3rem' }} data-testid={`time-spent-${idx}`}>
                      <Clock size={14} />
                      {question.time_spent}s
                    </div>
                  </div>
                </div>

                <div className="question-text" style={{ fontSize: '1rem', lineHeight: '1.6' }} data-testid={`question-text-${idx}`}>
                  <MathText>{question.question_text}</MathText>
                </div>

                {(question.image_url || question.image) && (
                  <img 
                    src={question.image_url || question.image} 
                    alt="Question diagram" 
                    style={{ 
                      maxWidth: 'min(400px, 100%)', 
                      width: 'auto',
                      height: 'auto',
                      margin: '1.5rem 0', 
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                )}

                {question.question_type === 'multiple_choice' ? (
                  <div className="choices">
                    {question.choices.map((choice) => {
                      const isUserAnswer = question.user_answer === choice;
                      const isCorrectAnswer = question.correct_answer === choice;
                      
                      let className = 'choice-button';
                      if (isCorrectAnswer) className += ' correct-answer';
                      if (isUserAnswer && !isCorrectAnswer) className += ' wrong-answer';
                      
                      return (
                        <div
                          key={choice}
                          className={className}
                          style={{
                            borderColor: isCorrectAnswer ? '#22c55e' : (isUserAnswer ? '#ef4444' : '#e0e0e0'),
                            borderWidth: (isCorrectAnswer || isUserAnswer) ? '3px' : '2px',
                            background: isCorrectAnswer ? '#dcfce7' : (isUserAnswer ? '#fee2e2' : '#f8f9fa'),
                            cursor: 'default'
                          }}
                          data-testid={`choice-${idx}-${choice}`}
                        >
                          <div className="choice-label" style={{
                            borderColor: isCorrectAnswer ? '#22c55e' : (isUserAnswer ? '#ef4444' : '#4a9b9e'),
                            background: (isCorrectAnswer || isUserAnswer) ? (isCorrectAnswer ? '#22c55e' : '#ef4444') : 'white',
                            color: (isCorrectAnswer || isUserAnswer) ? 'white' : '#4a9b9e'
                          }}>
                            {choice}
                          </div>
                          <div><MathText>{question.choice_texts[choice]}</MathText></div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Your Answer:</strong> 
                      <span style={{ 
                        marginLeft: '0.5rem',
                        color: question.is_correct ? '#22c55e' : '#ef4444',
                        fontWeight: '600'
                      }} data-testid={`user-answer-${idx}`}>
                        {question.user_answer || '(No answer)'}
                      </span>
                    </div>
                    <div>
                      <strong>Correct Answer:</strong> 
                      <span style={{ marginLeft: '0.5rem', color: '#22c55e', fontWeight: '600' }} data-testid={`correct-answer-${idx}`}>
                        {question.correct_answer}
                      </span>
                    </div>
                  </div>
                )}

                <div style={{ 
                  marginTop: '1.5rem', 
                  padding: '1.5rem', 
                  background: '#f8f9fa', 
                  borderRadius: '12px',
                  borderLeft: `4px solid ${getSectionColor()}`
                }}>
                  <strong style={{ color: getSectionColor() }}>Explanation:</strong>
                  <div style={{ marginTop: '0.5rem', lineHeight: '1.6', color: '#1a2332', whiteSpace: 'pre-wrap', maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }} data-testid={`explanation-${idx}`}>
                    {question.explanation ? (
                      <MathText>{question.explanation}</MathText>
                    ) : (
                      <span style={{ color: 'red' }}>No explanation available</span>
                    )}
                  </div>
                </div>

                {/* Why I Got It Wrong - Tag buttons for incorrect answers */}
                {!question.is_correct && (
                  <div data-testid={`mistake-tag-section-${idx}`} style={{ marginTop: '1rem', padding: '1rem', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#991b1b', marginBottom: '0.5rem' }}>Why did you get this wrong?</div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {tagOptions.map(t => {
                        const isActive = mistakeTags[question.id] === t.value;
                        return (
                          <button
                            key={t.value}
                            data-testid={`tag-${t.value}-${idx}`}
                            onClick={() => handleMistakeTag(question.id, t.value)}
                            style={{
                              padding: '0.35rem 0.7rem', borderRadius: 6, fontSize: '0.8rem', fontWeight: 500,
                              border: `1px solid ${t.color}`, cursor: 'pointer', transition: 'all 0.15s',
                              background: isActive ? t.color : '#fff',
                              color: isActive ? '#fff' : t.color
                            }}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Back Button */}
          <div style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '1rem' }}>
            <Button
              onClick={handleBackToReview}
              variant="outline"
              data-testid="back-to-review-bottom-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: getSectionColor(),
                borderColor: getSectionColor()
              }}
            >
              <ArrowLeft size={18} />
              Back to Review
            </Button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Dashboard?</DialogTitle>
            <DialogDescription>
              You can always review your test results from the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)} style={{ flex: 1 }}>
              Stay Here
            </Button>
            <Button onClick={handleExit} style={{ flex: 1, background: '#0030b3' }}>
              Go to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
